import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Textarea } from "@shared/components/ui/textarea";
import { Label } from "@shared/components/ui/label";
import { Star, MessageSquareHeart } from "lucide-react";
import { supabase } from "@lib/supabase/client";
import { useToast } from "@shared/hooks/use-toast";
import type { User } from "@supabase/supabase-js";

interface FeedbackRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
  requestId: string;
}

export const FeedbackRequestDialog = ({
  open,
  onOpenChange,
  user,
  requestId,
}: FeedbackRequestDialogProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    title: "",
    location: "",
    rating: 5,
    text: "",
  });

  // Mark as shown when dialog opens
  useEffect(() => {
    if (open && requestId) {
      supabase
        .from("feedback_requests")
        .update({ shown_at: new Date().toISOString() })
        .eq("id", requestId)
        .then();
    }
  }, [open, requestId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (formData.text.length < 20 || formData.text.length > 1000) {
      toast({
        title: "خطأ في النص",
        description: "يجب أن يكون النص بين 20 و 1000 حرف",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    // Insert testimonial
    const { error } = await supabase.from("testimonials").insert([
      {
        user_id: user.id,
        email: user.email,
        name: formData.name,
        title: formData.title,
        location: formData.location,
        rating: formData.rating,
        text: formData.text,
      },
    ]);

    if (error) {
      toast({
        title: "حدث خطأ",
        description: "فشل إرسال رأيك، يرجى المحاولة مرة أخرى",
        variant: "destructive",
      });
    } else {
      // Mark request as completed
      await supabase
        .from("feedback_requests")
        .update({ is_completed: true })
        .eq("id", requestId);

      toast({
        title: "شكراً لمشاركتك!",
        description: "تم إرسال رأيك بنجاح وسيتم عرضه بعد المراجعة",
      });

      setFormData({
        name: "",
        title: "",
        location: "",
        rating: 5,
        text: "",
      });
      onOpenChange(false);
    }

    setLoading(false);
  };

  const handleDismiss = async () => {
    // Mark as dismissed but not completed (can be shown again in future)
    await supabase
      .from("feedback_requests")
      .update({ dismissed_at: new Date().toISOString() })
      .eq("id", requestId);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center flex flex-col items-center">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <MessageSquareHeart className="h-8 w-8 text-primary" />
          </div>
          <DialogTitle className="text-2xl text-center w-full">شارك رأيك معنا</DialogTitle>
          <DialogDescription className="text-center w-full">
            نحن نقدر تجربتك ورأيك في خدماتنا. شاركنا تجربتك لنستمر في التطوير!
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">الاسم *</Label>
              <Input
                id="name"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="الاسم الكامل"
                maxLength={100}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">المسمى الوظيفي *</Label>
              <Input
                id="title"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="مثال: أخصائي تغذية"
                maxLength={100}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="location">المدينة *</Label>
              <Input
                id="location"
                required
                value={formData.location}
                onChange={(e) =>
                  setFormData({ ...formData, location: e.target.value })
                }
                placeholder="مثال: الرياض"
                maxLength={50}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rating">التقييم *</Label>
              <div className="flex items-center gap-1 h-10 px-3 py-2 border rounded-md bg-background">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFormData({ ...formData, rating: star })}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`h-5 w-5 transition-colors ${
                        star <= formData.rating
                          ? "text-yellow-500 fill-yellow-500"
                          : "text-muted-foreground"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="text">رأيك في خدماتنا *</Label>
            <Textarea
              id="text"
              required
              value={formData.text}
              onChange={(e) =>
                setFormData({ ...formData, text: e.target.value })
              }
              placeholder="شارك تجربتك مع خدماتنا... (بين 20 و 1000 حرف)"
              rows={4}
              minLength={20}
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground text-left">
              {formData.text.length} / 1000
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="submit" className="flex-1" disabled={loading}>
              {loading ? "جاري الإرسال..." : "إرسال الرأي"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleDismiss}
              disabled={loading}
            >
              لاحقاً
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            سيتم مراجعة رأيك قبل نشره على الموقع
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
